import { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Check, PlugZap } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TEAL } from "@/lib/copilot";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";

const CAN = ["Read bills, spending, savings goals, chores, piggy banks, wish lists, lists and family memory you can see", "Make changes you're allowed to make, like adding list items or approving chores"];

export default function ConnectAuthorize() {
  const { session } = useAuth();
  const [sp] = useSearchParams();
  const loc = useLocation();
  const [client, setClient] = useState(null);
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const q = Object.fromEntries(sp.entries());
  useEffect(() => {
    api.get(`/oauth/clients/${q.client_id}`).then(({ data }) => setClient(data)).catch(() => setError("This app's sign-in link isn't valid"));
  }, [q.client_id]);
  useEffect(() => { if (session?.organizations?.length === 1) setPicked([session.organizations[0].id]); }, [session]);
  if (session === null) return <Loader />;
  if (!session) {
    sessionStorage.setItem("qhh.returnTo", loc.pathname + loc.search);
    return <Navigate to="/signin" replace />;
  }
  const decide = async (allow) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/oauth/authorize", { ...q, organization_ids: picked, allow });
      window.location.assign(data.redirect);
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  };
  const kid = session.type === "kid";
  return (
    <div className="flex min-h-screen items-center justify-center bg-q-bg px-4 py-10" data-testid="oauth-consent-page">
      <div className="q-card w-full max-w-md">
        <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `rgba(${TEAL},0.15)` }}><PlugZap className="h-6 w-6" style={{ color: `rgb(${TEAL})` }} /></span>
        {kid ? <p className="font-display text-xl font-extrabold text-q-text" data-testid="oauth-kid-blocked">Only adults can connect an AI assistant</p> : !client ? (error ? <p className="q-error" data-testid="oauth-error">{error}</p> : <Loader />) : (
          <>
            <h1 className="font-display text-2xl font-extrabold text-q-text" data-testid="oauth-title"><span data-testid="oauth-client-name">{client.client_name}</span> wants to connect to The Queue</h1>
            <p className="mt-1 text-sm text-q-muted">Signed in as {session.user?.email}. It will act with your permissions.</p>
            <ul className="mt-4 space-y-1.5 text-sm text-q-muted">{CAN.map((c) => <li key={c} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: `rgb(${TEAL})` }} />{c}</li>)}</ul>
            <p className="mb-2 mt-5 text-[10px] font-black uppercase tracking-[0.18em] text-q-faint">Family hubs</p>
            <div className="space-y-1.5" data-testid="oauth-hubs">
              {(session.organizations || []).map((o) => (
                <label key={o.id} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-q-border px-3">
                  <input type="checkbox" className="h-5 w-5 accent-teal-500" checked={picked.includes(o.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, o.id] : picked.filter((x) => x !== o.id))} data-testid={`oauth-hub-${o.id}`} />
                  <span className="font-semibold text-q-text">{o.name}</span>
                </label>
              ))}
            </div>
            {error && <p className="q-error mt-3" data-testid="oauth-error">{error}</p>}
            <div className="mt-5 flex gap-2">
              <button className="q-btn q-btn-ghost min-h-[44px] flex-1" disabled={busy} onClick={() => decide(false)} data-testid="oauth-deny-btn">Deny</button>
              <button className="q-btn min-h-[44px] flex-1 font-extrabold" style={{ background: `rgb(${TEAL})`, color: "#042f2a" }} disabled={busy || !picked.length} onClick={() => decide(true)} data-testid="oauth-allow-btn">Allow</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
